import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Underline from "@tiptap/extension-underline";
import Strike from "@tiptap/extension-strike";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import FontSize from "./FontSize";
import CustomDropdown from "./CustomDropdown";
import Image from "@tiptap/extension-image";

import {
  BoldIcon,
  Italic,
  Palette,
  List,
  ListOrdered,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Heading1,
  Type,
  Link2,
  ExternalLink,
  Trash,
  Superscript as SupIcon,
  Subscript as SubIcon,
} from "lucide-react";

import { HexColorPicker } from "react-colorful";
import { createPortal } from "react-dom";
import { createPopper } from "@popperjs/core";
import "./TextEditorMail.css";

/* ─── helpers ─── */
const hexToRgba = (hex, o) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${o})`;
};

const alignOptions = [
  { Field: "left", icon: <AlignLeft size={14} /> },
  { Field: "center", icon: <AlignCenter size={14} /> },
  { Field: "right", icon: <AlignRight size={14} /> },
  { Field: "justify", icon: <AlignJustify size={14} /> },
];

/* ✅ Heading 1‑6 only (no Paragraph) */
const headingOptions = [
  ...[1, 2, 3, 4, 5, 6].map((n) => ({ Field: `heading-${n}` })),
];

const mentionList = ["Alice", "Bob", "Charlie"].map((l, i) => ({
  id: i + 1,
  label: l,
}));
const suggestion = {
  items: ({ query }) =>
    mentionList.filter((m) =>
      m.label.toLowerCase().includes(query.toLowerCase())
    ),
};

export default function TextEditorMail({ value, onChange, editorRef = null }) {
  /* ─── Tiptap editor ─── */
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: "tiptap-image",
          style: "max-width:40%;height:40%",
        },
      }),
      Underline,
      Strike,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Superscript,
      Subscript,
      Link.configure({
        openOnClick: false,
        autolink: false,
        HTMLAttributes: { class: "text-blue-600 underline" },
      }),
      Mention.configure({ suggestion }),
      FontSize,
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: "tiptap" } },
  });

  /* expose editor ref */
  useEffect(() => {
    if (editorRef) editorRef.current = editor;
    return () => {
      if (editorRef) editorRef.current = null;
    };
  }, [editor, editorRef]);

  /* ─── UI state ─── */
  const [fontSize, setFontSize] = useState("16px");
  const [alignment, setAlignment] = useState("left");
  const [colorOpen, setColorOpen] = useState(false);
  const [textColor, setTextColor] = useState("#000");
  const [opacity, setOpacity] = useState(1);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [selected, setSelected] = useState(""); 

  const colorBtn = useRef(null),
    colorPop = useRef(null);
  const linkBtn = useRef(null),
    linkPop = useRef(null);

  /* popper + outside‑click */
  useEffect(() => {
    const close = (e) => {
      if (
        colorOpen &&
        colorPop.current &&
        !colorPop.current.contains(e.target) &&
        colorBtn.current &&
        !colorBtn.current.contains(e.target)
      )
        setColorOpen(false);
      if (
        linkOpen &&
        linkPop.current &&
        !linkPop.current.contains(e.target) &&
        linkBtn.current &&
        !linkBtn.current.contains(e.target)
      )
        setLinkOpen(false);
    };
    if (colorOpen || linkOpen) document.addEventListener("mousedown", close);
    if (colorOpen && colorBtn.current && colorPop.current)
      createPopper(colorBtn.current, colorPop.current, {
        placement: "top-start",
        modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
      });
    if (linkOpen && linkBtn.current && linkPop.current)
      createPopper(linkBtn.current, linkPop.current, {
        placement: "top-start",
        modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
      });
    return () => document.removeEventListener("mousedown", close);
  }, [colorOpen, linkOpen]);

  /* heading select */
  const handleSelect = (val) => {
    if (!val) return;
    const level = parseInt(val.replace("heading-", ""));
    setSelected(val);
    editor.chain().focus().toggleHeading({ level }).run();
  };

  const getHeadingClass = (lvl) =>
    ["", "text-2xl", "text-xl", "text-lg", "text-base", "text-sm", "text-xs"][
      lvl
    ] || "text-sm";

  if (!editor) return null;

  /* ─── render ─── */
  return (
    <div className="relative h-full">
      <div className="h-full overflow-y-auto px-2 pt-2 pb-24">
        <EditorContent editor={editor} />
      </div>

      {/* Toolbar */}
      <div
        className="toolbar-scroll-wrapper absolute left-1/2 -translate-x-1/2 z-30"
        style={{
          bottom: "0.8rem",
          maxWidth: "73%",
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 6,
          padding: "2px 10px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.06)",
        }}
      >
        <div className="toolbar-inner">
          {/* bold/italic/underline */}
          <button
            className={`icon-btn ${editor.isActive("bold") ? "active" : ""}`}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <BoldIcon size={14} />
          </button>
          <button
            className={`icon-btn ${editor.isActive("italic") ? "active" : ""}`}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic size={14} />
          </button>
          <button
            className={`icon-btn ${
              editor.isActive("underline") ? "active" : ""
            }`}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <u className="text-[13px]">U</u>
          </button>

          {/* lists */}
          <button
            className={`icon-btn ${
              editor.isActive("bulletList") ? "active" : ""
            }`}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List size={16} />
          </button>
          <button
            className={`icon-btn ${
              editor.isActive("orderedList") ? "active" : ""
            }`}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered size={16} />
          </button>

          {/* color */}
          <button
            ref={colorBtn}
            className={`icon-btn ${colorOpen ? "active" : ""}`}
            onClick={() => setColorOpen(!colorOpen)}
          >
            <Palette size={16} />
          </button>

          {/* font‑size */}
          <CustomDropdown
            noSearch
            filteredFields={["12px", "14px", "16px", "18px", "24px"].map(
              (f) => ({ Field: f })
            )}
            addFilter={({ Field }) => {
              setFontSize(Field);
              editor.chain().focus().setFontSize({ fontSize: Field }).run();
            }}
            triggerIcon={
              <div
                className={`icon-btn ${fontSize !== "16px" ? "active" : ""}`}
              >
                {fontSize === "16px" ? <Type size={15} /> : fontSize}
              </div>
            }
          />

          {/* heading dropdown (H1‑H6) */}
          <CustomDropdown
            filteredFields={[...headingOptions]}
            addFilter={(f) => handleSelect(f.Field)}
            getColumnLabel={(f) => {
              const field = typeof f === "string" ? f : f.Field;
              const lvl = parseInt(field.replace("heading-", ""));
              const Tag = `h${lvl}`;
              return React.createElement(
                Tag,
                { className: `m-0 font-medium ${getHeadingClass(lvl)}` },
                `Heading ${lvl}`
              );
            }}
            noSearch
            triggerIcon={
              <div
                className={`icon-btn ${
                  selected.startsWith("heading-") || editor.isActive("heading")
                    ? "active"
                    : ""
                }`}
              >
                {selected.startsWith("heading-") ? (
                  `H ${selected.replace("heading-", "")}`
                ) : (
                  <Heading1 size={18} />
                )}
              </div>
            }
          />

          {/* strike */}
          <button
            className={`icon-btn ${editor.isActive("strike") ? "active" : ""}`}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <Strikethrough size={14} />
          </button>

          {/* alignment */}
          <CustomDropdown
            noSearch
            filteredFields={alignOptions.map((o) => ({ Field: o.Field }))}
            addFilter={({ Field }) => {
              setAlignment(Field);
              editor.chain().focus().setTextAlign(Field).run();
            }}
            getColumnLabel={(f) =>
              alignOptions.find((o) => o.Field === f)?.icon
            }
            triggerIcon={
              <div
                className={`icon-btn ${alignment !== "left" ? "active" : ""}`}
              >
                {alignOptions.find((o) => o.Field === alignment)?.icon}
              </div>
            }
          />

          {/* link */}
          <button
            ref={linkBtn}
            className={`icon-btn ${editor.isActive("link") ? "active" : ""}`}
            onClick={() => {
              setLinkUrl(editor.getAttributes("link")?.href || "");
              setLinkOpen(!linkOpen);
            }}
          >
            <Link2 size={16} />
          </button>

          {/* super/sub */}
          <button
            className={`icon-btn ${
              editor.isActive("superscript") ? "active" : ""
            }`}
            onClick={() =>
              editor.chain().focus().unsetSubscript().toggleSuperscript().run()
            }
          >
            <SupIcon size={15} />
          </button>
          <button
            className={`icon-btn ${
              editor.isActive("subscript") ? "active" : ""
            }`}
            onClick={() =>
              editor.chain().focus().unsetSuperscript().toggleSubscript().run()
            }
          >
            <SubIcon size={15} />
          </button>
        </div>
      </div>

      {/* color picker popover */}
      {colorOpen &&
        createPortal(
          <div ref={colorPop} className="dropdown-menu p-3">
            <HexColorPicker
              color={textColor}
              onChange={(c) => {
                setTextColor(c);
                editor.chain().focus().setColor(hexToRgba(c, opacity)).run();
              }}
            />
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={opacity}
              onChange={(e) => {
                setOpacity(e.target.value);
                editor
                  .chain()
                  .focus()
                  .setColor(hexToRgba(textColor, e.target.value))
                  .run();
              }}
              className="w-full mt-2"
            />
          </div>,
          document.body
        )}

      {/* link popover */}
      {linkOpen &&
        createPortal(
          <div ref={linkPop} className="dropdown-menu p-3 w-72">
            <input
              className="border px-2 py-1 rounded w-full text-sm mb-2"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://"
            />
            <div className="flex justify-between">
              <button
                onClick={() => {
                  editor.chain().focus().setLink({ href: linkUrl }).run();
                  setLinkOpen(false);
                }}
              >
                <Link2 size={16} />
              </button>
              <button
                onClick={() => {
                  const href = editor.getAttributes("link")?.href;
                  href && window.open(href, "_blank");
                }}
              >
                <ExternalLink size={16} />
              </button>
              <button
                onClick={() => {
                  editor.chain().focus().unsetLink().run();
                  setLinkOpen(false);
                }}
              >
                <Trash size={16} />
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
